// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import { HILABS_API } from '../../config-global'
import { GET_JWTToken } from '../../constant/auth'
import { dispatch } from '../../redux/store'
import { fetchLogout } from '../auth/slices'
// create a new mutex
const mutex = new Mutex()

const baseQuery = fetchBaseQuery({
  baseUrl: `${HILABS_API}`,

  prepareHeaders: function (headers) {
    let token = GET_JWTToken()
    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    return headers
  },
})

const baseQueryWithReauth = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()
      try {
        dispatch(fetchLogout())
      } finally {
        // release must be called once the mutex should be released again.
        release()
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }
  return result
}
// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: 'api',
  // All of our requests will have URLs starting with
  baseQuery: baseQueryWithReauth,
  // The "endpoints" represent operations and requests for this server
  endpoints: builder => ({
    // The `getConversations` endpoint is a "query" operation that returns data
    getConversations: builder.query({
      // The URL for the request is '/api/posts?limit=10&offset=0&orderBy=latest'
      query: ({ limit, offset, orderBy }) =>
        `/posts?limit=${limit}&offset=${offset}&orderBy=${orderBy}&commentsPreviewLimit=2`,
      providesTags: ['Post'],
    }),
    getConversation: builder.mutation({
      query: id => ({ url: `/posts/${id}`, method: 'GET' }),
      providesTags: ['SpecificPost'],
    }),
    addNewPost: builder.mutation({
      query: initialPost => ({
        url: '/posts',
        method: 'POST',
        body: initialPost,
      }),
      invalidatesTags: ['Post'],
    }),
    editPost: builder.mutation({
      query: ({ id, post }) => ({
        url: `/posts/${id}`,
        method: 'PUT',
        body: post,
      }),
      invalidatesTags: ['Post'],
    }),
    deletePost: builder.mutation({
      query: id => ({
        url: `/posts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Post'],
    }),
    reactToPost: builder.mutation({
      query: ({ id, reactionType }) => ({
        url: `/posts/${id}/react?reactionType=${reactionType}`,
        method: 'POST',
      }),
      // invalidatesTags: ['Post', 'SpecificPost'],
    }),
    removeReactionToPost: builder.mutation({
      query: ({ id, reactionId }) => ({
        url: `/posts/${id}/react/${reactionId}`,
        method: 'DELETE',
      }),
      // invalidatesTags: ['Post', 'SpecificPost'],
    }),
    reactToComment: builder.mutation({
      query: ({ id, reactionType }) => ({
        url: `/comments/${id}/react?reactionType=${reactionType}`,
        method: 'POST',
      }),
      // invalidatesTags: ['SpecificPost', 'Post'],
    }),
    removeReactionToComment: builder.mutation({
      query: ({ id, reactionId }) => ({
        url: `/comments/${id}/react/${reactionId}`,
        method: 'DELETE',
      }),
      // invalidatesTags: ['SpecificPost', 'Post'],
    }),
    uploadPostAttachment: builder.mutation({
      query: file => {
        let bodyFormData = new FormData()
        bodyFormData.append('attachment', file)
        return {
          url: '/posts/upload',
          method: 'POST',
          body: bodyFormData,
          formData: true,
        }
      },
    }),
    deletePostAttachment: builder.mutation({
      query: id => ({
        url: `/posts/deleteUpload/${id}`,
        method: 'DELETE',
      }),
    }),
    validateUser: builder.mutation({
      query: token => {
        return {
          url: 'auth/validate-user',
          method: 'GET',
          headers: {
            'auth-type': 'ms',
            'ms-token': token,
          },
        }
      },
    }),
    addNewComment: builder.mutation({
      query: comment => ({
        url: '/comments',
        method: 'POST',
        body: comment,
      }),
      // invalidatesTags: ['SpecificPost', 'Post'],
    }),
    deleteComment: builder.mutation({
      query: ({ postId, commentId }) => ({
        url: `/comments/${postId}/${commentId}`,
        method: 'DELETE',
      }),
      // invalidatesTags: ['SpecificPost', 'Post'],
    }),
    updateComment: builder.mutation({
      query: ({ postId, commentId, comment }) => ({
        url: `/comments/${commentId}`,
        method: 'PUT',
        body: {
          content: comment,
          attachmentsList: [],
        },
      }),
      // invalidatesTags: ['SpecificPost', 'Post'],
    }),
    submitQuiz: builder.mutation({
      query: formData => ({
        url: '/quiz/submit',
        method: 'POST',
        body: formData,
      }),
    }),
    getHipaaQuestions: builder.mutation({
      query: id => ({ url: `/quiz/questions`, method: 'GET' }),
    }),
  }),
})

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetConversationsQuery,
  useGetConversationMutation,
  useAddNewPostMutation,
  useUploadPostAttachmentMutation,
  useReactToPostMutation,
  useRemoveReactionToPostMutation,
  useReactToCommentMutation,
  useRemoveReactionToCommentMutation,
  useValidateUserMutation,
  useAddNewCommentMutation,
  useEditPostMutation,
  useDeletePostMutation,
  useDeleteCommentMutation,
  useUpdateCommentMutation,
  useGetHipaaQuestionsMutation,
  useSubmitQuizMutation,
} = apiSlice
